import { getCountryName } from "./constants";

// Re-exported for backwards compatibility; implementations moved to
// numberFormatHelper to break the StringHelper <-> constants import cycle.
export { getCurrentCustomerCount, formatNumberToShortForm } from "./numberFormatHelper";

export const convertSpacesToDashes = (str: string) => {
  return (str && str.replace(/\s+/g, "-")) || "";
};
export const toRegionSuburbCase = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

export const toProperCase = (str: string) =>
  str && str.charAt(0).toUpperCase() + str.substr(1).toLowerCase();

/** Title-case each hyphenated segment, keep hyphens (for URL slugs). */
export const titleCaseSlug = (slug: string) =>
  slug
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join("-");

/** Area page query `country`: 2-letter codes uppercase (US, UK); longer → first letter uppercase, rest lower. */
export const formatAreaQueryCountryParam = (code: string) => {
  if (!code || code === "reset") return code;
  const t = decodeURIComponent(String(code)).trim().toLowerCase();
  if (!t) return code;
  if (t.length <= 2) return t.toUpperCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
};

/** Area page query `state`: short codes (e.g. NSW, TX); longer slugs hyphen title-case. */
export const formatAreaQueryStateParam = (state: string) => {
  if (!state || state === "reset") return state;
  const decoded = decodeURIComponent(String(state));
  return decoded.length <= 3 ? decoded.toUpperCase() : titleCaseSlug(decoded);
};

/** Area page query `city`: hyphen title-case (e.g. inner-west → Inner-West). */
export const formatAreaQueryCityParam = (city: string) => {
  if (!city || city === "reset") return city;
  return titleCaseSlug(decodeURIComponent(String(city)));
};

/** Compare city/suburb to state ignoring case and hyphenation (e.g. nsw vs NSW). */
export const normalizeAreaLocationKey = (value?: string | null) => {
  if (!value) return "";
  return decodeURIComponent(String(value)).trim().toLowerCase().replace(/-/g, " ");
};

export const isAreaCityDistinctFromState = (
  city?: string | null,
  state?: string | null,
) => {
  const cityKey = normalizeAreaLocationKey(city);
  const stateKey = normalizeAreaLocationKey(state);
  return Boolean(cityKey && (!stateKey || cityKey !== stateKey));
};

/** Single location segment for area H1: city, else state, else country (never combined). */
export const resolveAreaOfficeLocationLabel = (options: {
  paramCity?: string;
  paramState?: string;
  paramCountry?: string;
  areaCity?: string;
  areaState?: string;
  areaCountry?: string;
  routeCountry?: string;
  hasAnyLocationParams: boolean;
}) => {
  const {
    paramCity = "",
    paramState = "",
    paramCountry = "",
    areaCity = "",
    areaState = "",
    areaCountry = "",
    routeCountry = "",
    hasAnyLocationParams,
  } = options;

  const effectiveState = hasAnyLocationParams ? paramState : areaState;
  const rawCity = hasAnyLocationParams ? paramCity : areaCity;
  const effectiveCity = isAreaCityDistinctFromState(rawCity, effectiveState)
    ? rawCity
    : "";

  if (effectiveCity) {
    return titleCaseSlug(decodeURIComponent(effectiveCity)).replace(/-/g, " ");
  }
  if (effectiveState) {
    const decoded = decodeURIComponent(effectiveState);
    return decoded.length <= 3 ? decoded.toUpperCase() : titleCaseSlug(decoded).replace(/-/g, " ");
  }

  const effectiveCountry = hasAnyLocationParams
    ? paramCountry
    : areaCountry || routeCountry;
  if (!effectiveCountry) return "";
  const formatted = formatAreaQueryCountryParam(effectiveCountry);
  return formatted.length <= 2 ? getCountryName(formatted) : formatted;
};

const formatAreaCityLabel = (city: string) =>
  titleCaseSlug(decodeURIComponent(city)).replace(/-/g, " ");

const formatAreaStateLabel = (state: string) => {
  const decoded = decodeURIComponent(String(state));
  return decoded.length <= 3
    ? decoded.toUpperCase()
    : titleCaseSlug(decoded).replace(/-/g, " ");
};

/** e.g. "Perth, WA" for listing subheadings (city + state when both apply). */
export const resolveAreaListingPlaceLabel = (options: {
  paramCity?: string;
  paramState?: string;
  areaCity?: string;
  areaState?: string;
  hasAnyLocationParams: boolean;
}) => {
  const effectiveState = options.hasAnyLocationParams
    ? options.paramState
    : options.areaState;
  const rawCity = options.hasAnyLocationParams
    ? options.paramCity
    : options.areaCity;
  const effectiveCity = isAreaCityDistinctFromState(rawCity, effectiveState)
    ? rawCity
    : "";

  if (effectiveCity) {
    const city = formatAreaCityLabel(effectiveCity);
    if (effectiveState) {
      return `${city}, ${formatAreaStateLabel(effectiveState)}`;
    }
    return city;
  }
  if (effectiveState) {
    return formatAreaStateLabel(effectiveState);
  }
  return "";
};

/** H1 place: "Sydney, NSW" when city exists; else state; else country. */
export const resolveAreaListingPlaceForHeadline = (options: {
  paramCity?: string;
  paramState?: string;
  paramCountry?: string;
  areaCity?: string;
  areaState?: string;
  areaCountry?: string;
  routeCountry?: string;
  hasAnyLocationParams: boolean;
}) => {
  const listingPlace = resolveAreaListingPlaceLabel({
    paramCity: options.paramCity,
    paramState: options.paramState,
    areaCity: options.areaCity,
    areaState: options.areaState,
    hasAnyLocationParams: options.hasAnyLocationParams,
  });
  if (listingPlace) return listingPlace;
  return resolveAreaOfficeLocationLabel(options);
};

/**
 * H2 place follows the H1 location level: country or state only when the title does;
 * expands to "City, State" only when the title shows the city.
 */
export const resolveAreaListingPlaceForSubheadline = (options: {
  paramCity?: string;
  paramState?: string;
  paramCountry?: string;
  areaCity?: string;
  areaState?: string;
  areaCountry?: string;
  routeCountry?: string;
  hasAnyLocationParams: boolean;
}) => {
  const titlePlace = resolveAreaOfficeLocationLabel(options);
  if (!titlePlace) return "";

  const effectiveState = options.hasAnyLocationParams
    ? options.paramState
    : options.areaState;
  const rawCity = options.hasAnyLocationParams
    ? options.paramCity
    : options.areaCity;
  const effectiveCity = isAreaCityDistinctFromState(rawCity, effectiveState)
    ? rawCity
    : "";

  if (
    effectiveCity &&
    effectiveState &&
    titlePlace === formatAreaCityLabel(effectiveCity)
  ) {
    return (
      resolveAreaListingPlaceLabel({
        paramCity: options.paramCity,
        paramState: options.paramState,
        areaCity: options.areaCity,
        areaState: options.areaState,
        hasAnyLocationParams: options.hasAnyLocationParams,
      }) || titlePlace
    );
  }

  return titlePlace;
};

export type AreaBreadcrumbLevel = "country" | "state" | "city";

/** Params that indicate the user has moved past the initial location landing view. */
const GEOSearch_SUBSEQUENT_LOAD_PARAM_KEYS = [
  "minPrice",
  "maxPrice",
  "minPeople",
  "maxPeople",
  "amenities",
  "facilities",
  "promo",
  "workspaceId",
  "workspaceType",
  "minLatitude",
  "maxLatitude",
  "minLongitude",
  "maxLongitude",
  "latitude",
  "longitude",
  "zoom",
  "officeTypeFilterTouched",
] as const;

/** Infer API `firstPageLoad` from visible URL params (never stored in the browser URL). */
export function inferIsFirstPageLoad(
  searchParams: URLSearchParams,
  options?: {
    defaultSortBy?: string;
    defaultOrder?: string;
  },
): boolean {
  const page = searchParams.get("page");
  if (page && page !== "1") {
    return false;
  }

  for (const key of GEOSearch_SUBSEQUENT_LOAD_PARAM_KEYS) {
    const value = searchParams.get(key);
    if (value == null || value.trim() === "" || value.toLowerCase() === "reset") {
      continue;
    }
    if (key === "workspaceType" && value === "All") {
      continue;
    }
    return false;
  }

  const sortBy = searchParams.get("sortBy");
  const order = searchParams.get("order");
  const defaultSortBy = options?.defaultSortBy;
  const defaultOrder = options?.defaultOrder;

  if (sortBy && defaultSortBy && sortBy !== defaultSortBy) {
    return false;
  }
  if (order && defaultOrder && order !== defaultOrder) {
    return false;
  }

  return true;
}

/** Omit `page` from the URL when it is 1 (first page is the default). */
export function applyPageSearchParam(
  params: URLSearchParams,
  page: number | string | null | undefined,
): void {
  if (page === null || page === undefined || page === "" || page === "reset") {
    params.delete("page");
    return;
  }
  const pageNum = typeof page === "number" ? page : parseInt(String(page), 10);
  if (!Number.isNaN(pageNum) && pageNum > 1) {
    params.set("page", String(pageNum));
  } else {
    params.delete("page");
  }
}

/** Crawlable pagination href that omits `page=1`. */
export function buildSearchPageHref(
  pathname: string,
  searchParams: URLSearchParams,
  page: number,
): string {
  const params = new URLSearchParams(searchParams);
  applyPageSearchParam(params, page);
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

export function stripDefaultPageSearchParam(params: URLSearchParams): boolean {
  if (params.get("page") === "1") {
    params.delete("page");
    return true;
  }
  return false;
}

/** Params that do not affect loader/API data — safe to strip from the visible URL. */
export function stripCosmeticGeosearchSearchParams(params: URLSearchParams): void {
  params.delete("firstPageLoad");
  stripDefaultPageSearchParam(params);
}

export function geosearchSearchStringsEquivalent(
  leftSearch: string,
  rightSearch: string,
): boolean {
  const left = new URLSearchParams(leftSearch.startsWith("?") ? leftSearch.slice(1) : leftSearch);
  const right = new URLSearchParams(rightSearch.startsWith("?") ? rightSearch.slice(1) : rightSearch);
  stripCosmeticGeosearchSearchParams(left);
  stripCosmeticGeosearchSearchParams(right);
  return left.toString() === right.toString();
}

/** Stable browser URL param order for area geosearch pages. */
export const AREA_PAGE_QUERY_KEY_ORDER = [
  "country",
  "scope",
  "state",
  "city",
  "page",
  "firstPageLoad",
  "workspaceType",
  "officeTypeFilterTouched",
  "minPrice",
  "maxPrice",
  "minPeople",
  "maxPeople",
  "amenities",
  "facilities",
  "workspaceId",
  "promo",
  "sale",
  "sortBy",
  "order",
  "latitude",
  "longitude",
  "zoom",
  "minLatitude",
  "maxLatitude",
  "minLongitude",
  "maxLongitude",
] as const;

export const AREA_BREADCRUMB_LOCATION_PARAM_KEYS = [
  "country",
  "scope",
  "state",
  "city",
  "page",
  "firstPageLoad",
] as const;

/** Params safe to carry on breadcrumb navigation (location + explicit office-type choice). */
export const AREA_BREADCRUMB_PRESERVED_PARAM_KEYS = [
  ...AREA_BREADCRUMB_LOCATION_PARAM_KEYS,
  "workspaceType",
  "officeTypeFilterTouched",
  "sortBy",
  "order",
] as const;

export type AreaBreadcrumbOfficeTypeParams = {
  workspaceType?: string;
  officeTypeFilterTouched?: string;
};

export type AreaBreadcrumbSortParams = {
  sortBy: string;
  order?: string;
};

/** Reset office-type filter when navigating back via area breadcrumbs. */
export const resolveAreaBreadcrumbOfficeTypeParams = (
  _searchParams: URLSearchParams,
): AreaBreadcrumbOfficeTypeParams => {
  return { workspaceType: "All" };
};

/** Keep active sort when navigating via area breadcrumbs. */
export const resolveAreaBreadcrumbSortParams = (
  searchParams: URLSearchParams,
): AreaBreadcrumbSortParams | undefined => {
  const sortBy = searchParams.get("sortBy");
  if (!sortBy) return undefined;
  const order = searchParams.get("order");
  return order ? { sortBy, order } : { sortBy };
};

/** Whether the breadcrumb trail should include the office-type level (deepest crumb). */
export const shouldShowAreaBreadcrumbOfficeLevel = (
  searchParams: URLSearchParams,
  hasLocationQueryParams: boolean,
): boolean => {
  if (!hasLocationQueryParams) {
    return true;
  }

  const workspaceType = searchParams.get("workspaceType");
  if (workspaceType === "All") {
    return false;
  }
  if (workspaceType) {
    return true;
  }

  if (searchParams.get("officeTypeFilterTouched") === "1") {
    return false;
  }

  if (searchParams.get("firstPageLoad") === "false") {
    return false;
  }

  return true;
};

const appendAreaBreadcrumbOfficeTypeParams = (
  params: URLSearchParams,
  officeType?: AreaBreadcrumbOfficeTypeParams,
) => {
  if (officeType?.workspaceType) {
    params.set("workspaceType", officeType.workspaceType);
  }
  if (officeType?.officeTypeFilterTouched === "1") {
    params.set("officeTypeFilterTouched", "1");
  }
};

const appendAreaBreadcrumbSortParams = (
  params: URLSearchParams,
  sort?: AreaBreadcrumbSortParams,
) => {
  if (!sort?.sortBy) return;
  params.set("sortBy", sort.sortBy);
  if (sort.order) {
    params.set("order", sort.order);
  }
};

const appendCityBreadcrumbCoords = (
  params: URLSearchParams,
  latitude?: number | string | null,
  longitude?: number | string | null,
) => {
  const lat = latitude != null && latitude !== "" ? Number(latitude) : NaN;
  const lng = longitude != null && longitude !== "" ? Number(longitude) : NaN;
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    params.set("latitude", String(lat));
    params.set("longitude", String(lng));
  }
};

/** Canonical query params for an area breadcrumb level (single source of truth). */
export const buildAreaBreadcrumbSearchParams = (
  level: AreaBreadcrumbLevel,
  options: {
    countryCode: string;
    state?: string;
    city?: string;
    officeType?: AreaBreadcrumbOfficeTypeParams;
    sort?: AreaBreadcrumbSortParams;
    latitude?: number | string | null;
    longitude?: number | string | null;
  },
) => {
  const params = new URLSearchParams();

  switch (level) {
    case "country":
      params.set("country", formatAreaQueryCountryParam(options.countryCode));
      params.set("scope", "country");
      params.set("page", "1");
      params.set("firstPageLoad", "false");
      break;
    case "state":
      if (!options.state) return params;
      params.set(
        "state",
        formatAreaQueryStateParam(decodeURIComponent(options.state)),
      );
      params.set("page", "1");
      params.set("firstPageLoad", "true");
      break;
    case "city":
      if (!options.state || !options.city) return params;
      params.set(
        "state",
        formatAreaQueryStateParam(decodeURIComponent(options.state)),
      );
      params.set(
        "city",
        formatAreaQueryCityParam(decodeURIComponent(options.city)),
      );
      params.set("page", "1");
      params.set("firstPageLoad", "false");
      appendCityBreadcrumbCoords(
        params,
        options.latitude,
        options.longitude,
      );
      break;
  }

  appendAreaBreadcrumbOfficeTypeParams(params, options.officeType);
  appendAreaBreadcrumbSortParams(params, options.sort);
  return params;
};

export const serializeAreaPageSearchParams = (params: URLSearchParams) => {
  const ordered = new URLSearchParams();
  for (const key of AREA_PAGE_QUERY_KEY_ORDER) {
    if (params.has(key)) {
      ordered.set(key, params.get(key)!);
    }
  }
  params.forEach((value, key) => {
    if (!AREA_PAGE_QUERY_KEY_ORDER.includes(key as (typeof AREA_PAGE_QUERY_KEY_ORDER)[number])) {
      ordered.set(key, value);
    }
  });
  stripDefaultPageSearchParam(ordered);
  return ordered.toString();
};

export const resolveAreaBreadcrumbLevelFromOptions = (
  options: Record<string, string>,
): AreaBreadcrumbLevel | null => {
  if (options.scope === "country") return "country";
  if (options.city && options.city !== "reset") return "city";
  if (options.state && options.state !== "reset") return "state";
  if (options.country && options.country !== "reset") return "country";
  return null;
};

export const isAreaBreadcrumbNavigationOptions = (
  options: Record<string, string>,
) => {
  const keys = Object.keys(options);
  if (keys.length === 0) return false;

  const filterKeys = new Set([
    "workspaceType",
    "officeTypeFilterTouched",
    "minPrice",
    "maxPrice",
    "minPeople",
    "maxPeople",
    "amenities",
    "facilities",
    "workspaceId",
    "promo",
    "sale",
    "sortBy",
    "order",
    "reset",
  ]);

  if (keys.some((k) => filterKeys.has(k))) return false;
  return resolveAreaBreadcrumbLevelFromOptions(options) != null;
};

export const inferAreaBreadcrumbLevelFromSearchParams = (
  searchParams: URLSearchParams,
): AreaBreadcrumbLevel | null => {
  if (searchParams.get("scope") === "country") return "country";
  if (searchParams.get("city")) return "city";
  if (searchParams.get("state")) return "state";
  if (searchParams.get("country")) return "country";
  return null;
};

export const isLocationOnlyAreaSearchParams = (searchParams: URLSearchParams) => {
  if ([...searchParams.keys()].length === 0) return false;

  // User-applied filters (office type, price, etc.) are not breadcrumb browse-only URLs.
  if (searchParams.get("officeTypeFilterTouched") === "1") return false;

  const workspaceType = searchParams.get("workspaceType");
  if (workspaceType && workspaceType !== "All") return false;

  let preservedOnly = true;
  searchParams.forEach((_, key) => {
    if (
      !AREA_BREADCRUMB_PRESERVED_PARAM_KEYS.includes(
        key as (typeof AREA_BREADCRUMB_PRESERVED_PARAM_KEYS)[number],
      )
    ) {
      preservedOnly = false;
    }
  });
  return preservedOnly;
};

/** Canonical area-page breadcrumb URL for a location level (matches AreaBreadcrumbs / callGeoSearch). */
export const buildAreaBreadcrumbLevelUrl = (
  pathname: string,
  level: AreaBreadcrumbLevel,
  options: {
    countryCode: string;
    state?: string;
    city?: string;
    officeType?: AreaBreadcrumbOfficeTypeParams;
    sort?: AreaBreadcrumbSortParams;
    latitude?: number | string | null;
    longitude?: number | string | null;
  },
) => {
  const params = buildAreaBreadcrumbSearchParams(level, options);
  const qs = serializeAreaPageSearchParams(params);
  return qs ? `${pathname}?${qs}` : pathname;
};

export const replaceBackslashes = (str: string | null | undefined = ""): string => {
  if (str == null) {
    return "";
  }
  return str
    .replace(/"\\/g, "")
    .replace(/"/g, "'")
    .replace(/\n/g, "")
    .replace(/\\/g, " ");
};

export const decimalFormatter = (number = 0, digits = 2, currency = "USD") => {
  const formattedCurrency = new Intl.NumberFormat(undefined, {
    style: "decimal",
    currency: currency,
    minimumFractionDigits: digits
  }).format(number);

  return formattedCurrency;
};

export const extractCity = (location: string) => {
  // Handles multi-word cities
  const parts = location.split(' ');
  if (parts.length > 1) {
    return parts.slice(0, -1).join(' ');
  }
  return location;
}

// Utility functions
export const parseSearchUrl = (url: string) => {
  const [path, queryString] = url?.split("?")
  const pathParts = path.split("/").filter(Boolean)
  const [country, section, state, city] = pathParts
  const queryParams = Object.fromEntries(new URLSearchParams(queryString || ""))

  return {
    country,
    section,
    state,
    city,
    ...Object.fromEntries(
      Object.entries(queryParams).map(([key, value]) => {
        const num = Number.parseFloat(value as string)
        return [key, isNaN(num) ? value : num]
      }),
    )
  }
}

export const toQueryString = (obj: Record<string, string | number | undefined>) => {
  return Object.entries(obj)
    .filter(([, value]) => {
      if (value === null || value === undefined) return false
      if (Array.isArray(value) && value.length === 0) return false
      if (typeof value === "string" && value.trim() === "") return false
      return true
    })
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join("&")
}